import {
  LCADL_OPTIONS,
  LCADL_SECTIONS,
  type LCADLResultPayload,
  type LCADLSectionKey,
} from '../tests/LCADLTest';
import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';
import QuestionnaireResultHero from '../components/QuestionnaireResultHero';

export const getLCADLSeverityLabel = (percentage?: number | null): string => {
  if (typeof percentage !== 'number') return '—';
  return 'Limitação nas atividades diárias';
};

export const getLCADLSeverityTone = (percentage?: number | null): string => {
  if (typeof percentage !== 'number') return 'bg-slate-100 text-slate-700';
  return 'bg-slate-100 text-slate-700';
};

export const getLCADLScoreColor = (percentage?: number | null): string => {
  if (typeof percentage !== 'number') return '#475569';
  return '#0f766e';
};

export const getLCADLInterpretationText = (percentage?: number | null): string => {
  if (typeof percentage !== 'number') {
    return 'Não há dados suficientes para interpretar o LCADL.';
  }
  return `O percentual aplicável foi ${percentage.toFixed(1)}%. Valores maiores representam maior limitação por dispneia; não foi aplicada faixa automática de gravidade.`;
};

const SECTION_KEYS = Object.keys(LCADL_SECTIONS) as LCADLSectionKey[];

const getSectionPercent = (section?: { score?: number; maxScore?: number } | null): number | null => {
  if (!section || typeof section.score !== 'number' || typeof section.maxScore !== 'number' || section.maxScore <= 0) return null;
  return Number(((section.score / section.maxScore) * 100).toFixed(1));
};

const getAnswerLabel = (answer?: number | null): string => {
  if (typeof answer !== 'number') return 'Não respondido';
  return LCADL_OPTIONS.find((option) => option.value === answer)?.label ?? 'Não respondido';
};

interface LCADLResultRendererProps {
  payload: Partial<LCADLResultPayload> | null | undefined;
}

export default function LCADLResultRenderer({ payload }: LCADLResultRendererProps) {
  const totalScore = typeof payload?.totalScore === 'number' ? payload.totalScore : null;
  const maxScore = typeof payload?.maxScore === 'number' ? payload.maxScore : null;
  const percentage = typeof payload?.percentage === 'number' ? payload.percentage : null;
  const severity = getLCADLSeverityLabel(percentage);
  const scoreColor = getLCADLScoreColor(percentage);
  const interpretation = resolveAssessmentInterpretationText(payload, getLCADLInterpretationText(percentage));
  const isValidatedVersion = payload?.instrumentVersion === 'LCADL_BR_15_CARPES_2008'
    && Array.isArray(payload.answers)
    && payload.answers.length === 15;

  const flattenedRows = SECTION_KEYS.flatMap((sectionKey) => {
    const section = payload?.[sectionKey] as LCADLResultPayload[LCADLSectionKey] | undefined;
    if (!section) return [];
    return section.activities.map((activity, index) => ({
      sectionKey,
      sectionTitle: LCADL_SECTIONS[sectionKey].title,
      activity,
      answer: section.answers[index],
    }));
  });

  return (
    <div className="space-y-6">
      <QuestionnaireResultHero
        title="Pontuação LCADL"
        value={totalScore ?? '—'}
        valueColor={scoreColor}
        valueCaption={maxScore != null ? `${totalScore ?? '—'} / ${maxScore}` : 'Pontuação indisponível'}
        classification={severity}
        supportingText={typeof percentage === 'number' ? `${percentage.toFixed(1)}% do máximo aplicável` : undefined}
        interpretation={interpretation}
      >
          <div className="space-y-2">
            <div className="relative py-1">
              <div
                className="h-9 rounded-full"
                style={{
                  background: 'linear-gradient(90deg, #ccfbf1 0%, #0f766e 100%)',
                }}
              />
              {typeof percentage === 'number' && (
                <div
                  className="absolute -top-2 h-[calc(100%+16px)] w-1 rounded-full bg-slate-800"
                  style={{ left: `calc(${Math.max(0, Math.min(100, percentage))}% - 2px)` }}
                />
              )}
            </div>
            <div className="flex items-center justify-between text-xs font-medium text-slate-500">
              <span>Menor limitação</span>
              <span>Maior limitação</span>
            </div>
          </div>
      </QuestionnaireResultHero>

      {!isValidatedVersion && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900">
          Versão legada adaptada de 12 itens: o registro foi preservado sem conversão para a LCADL brasileira validada de 15 itens e sem reclassificação retroativa.
        </div>
      )}

      <div className="overflow-hidden rounded-[24px] border border-slate-100 shadow-sm">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">
              <th className="px-6 py-4">Domínio</th>
              <th className="px-6 py-4 text-center">Pontuação</th>
              <th className="px-6 py-4 text-center">Máximo</th>
              <th className="px-6 py-4 text-center">Impacto</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {SECTION_KEYS.map((sectionKey) => {
              const section = payload?.[sectionKey] as LCADLResultPayload[LCADLSectionKey] | undefined;
              const sectionPercent = getSectionPercent(section);
              return (
                <tr key={sectionKey} className="hover:bg-slate-50/60">
                  <td className="px-6 py-4 font-bold text-slate-700">{LCADL_SECTIONS[sectionKey].title}</td>
                  <td className="px-6 py-4 text-center font-black text-slate-900">{section?.score ?? '—'}</td>
                  <td className="px-6 py-4 text-center text-slate-700">{section?.maxScore ?? '—'}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex rounded-lg px-2.5 py-1 text-[10px] font-black uppercase ${getLCADLSeverityTone(sectionPercent)}`}>
                      {sectionPercent != null ? `${sectionPercent.toFixed(1)}%` : '—'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="overflow-hidden rounded-[24px] border border-slate-100 shadow-sm">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">
              <th className="px-6 py-4">Atividade</th>
              <th className="px-6 py-4">Domínio</th>
              <th className="px-6 py-4 text-center">Pontuação</th>
              <th className="px-6 py-4">Resposta selecionada</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {flattenedRows.map((row) => (
              <tr key={`${row.sectionKey}-${row.activity}`} className="hover:bg-slate-50/60">
                <td className="px-6 py-4 font-medium text-slate-700">{row.activity}</td>
                <td className="px-6 py-4 text-slate-600">{row.sectionTitle}</td>
                <td className="px-6 py-4 text-center font-black text-slate-900">{row.answer ?? '—'}</td>
                <td className="px-6 py-4 text-slate-700">{getAnswerLabel(row.answer)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
